import React, { useCallback, useRef, useState } from 'react';
import { useInView } from 'framer-motion';
import { MousePointerClickIcon } from 'lucide-react';
import { Reveal } from './Reveal';
import { CTAButton } from './CTAButton';
import { PendingArtwork } from './PendingArtwork';
import { PreviewLightbox } from './PreviewLightbox';
import { PREVIEW_PDF_URL, previewItems } from '../data/previewItems';
import { usePdfPageImages } from '../hooks/usePdfPageImages';
export function PreviewSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const nearViewport = useInView(sectionRef, {
    once: true,
    margin: '800px 0px 800px 0px'
  });
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const triggerRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const {
    pages,
    status
  } = usePdfPageImages(PREVIEW_PDF_URL, nearViewport);
  const items = previewItems.map(item => ({
    ...item,
    image: pages[item.page - 1] ?? null
  }));
  const handleClose = useCallback(() => {
    setOpenIndex(current => {
      if (current !== null) {
        const trigger = triggerRefs.current[current];
        requestAnimationFrame(() => trigger?.focus());
      }
      return null;
    });
  }, []);
  const placeholderLabel = status === 'error' ? 'Extrait momentanément indisponible' : 'Chargement de l’extrait…';
  return <section ref={sectionRef} id="extraits" aria-labelledby="preview-title" className="scroll-mt-9 bg-brand-ivory px-4 py-20 md:py-28" data-id="element-222" data-mp="2gt35l1xo9te9">
      <Reveal className="mx-auto max-w-6xl" data-id="element-223" data-mp="1t1nxstn4mjvm">
        <div className="mb-10 text-center" data-id="element-224" data-mp="1p384y94d6haa">
          <p className="mb-3 text-[11px] font-extrabold uppercase tracking-[0.16em] text-brand-teal-ink" data-id="element-225" data-mp="1p0c5uhgs5mk9">
            À L’INTÉRIEUR DE LA COLLECTION
          </p>
          <h2 id="preview-title" className="text-3xl font-extrabold tracking-tight text-brand-navy md:text-4xl" data-id="element-226" data-mp="1y225uboq9ttn">
            Découvrez de vraies pages des tomes.
          </h2>
          <p className="mt-4 text-sm text-slate-600 md:text-base" data-id="element-227" data-mp="15ezdy2ek4hcd">Des extraits des PDF de MUSIQUE PRÊTE.</p>
          <p className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-brand-teal-ink" data-id="element-228" data-mp="35hgyvce21f3v">
            <MousePointerClickIcon className="h-4 w-4" aria-hidden="true" data-id="element-229" data-mp="3aeqlyldz1eoj" />
            Cliquez sur une page pour l’agrandir
          </p>
        </div>
        <ul className="flex flex-wrap justify-center gap-5" data-id="element-230" data-mp="3f8uuva6srws9">
          {items.map(({
          image,
          alt,
          caption
        }, index) => <li key={caption} className="w-full max-w-sm rounded-2xl bg-white p-3 shadow-lg shadow-brand-navy/5 sm:w-[calc(50%-0.625rem)] sm:max-w-none md:w-[calc((100%-2.5rem)/3)]" data-id="element-231" data-mp="2mi32knsursa7" data-mpi={index}>
              <button ref={node => {
            triggerRefs.current[index] = node;
          }} type="button" onClick={() => setOpenIndex(index)} aria-label={`Agrandir l’extrait\u00A0: ${caption}`} aria-haspopup="dialog" className="block w-full cursor-zoom-in overflow-hidden rounded-xl bg-brand-ivory ring-1 ring-brand-line transition-shadow duration-150 ease-out hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal focus-visible:ring-offset-2" data-id="element-232" data-mp="2wexycyxqreks">
                {image ? <img src={image.src} alt={alt} width={image.width} height={image.height} loading="lazy" decoding="async" className="aspect-[3/4] h-auto w-full bg-white object-cover object-top" data-id="element-233" data-mp="1rezphinrcppi" /> : <PendingArtwork label={placeholderLabel} className={`aspect-[3/4] w-full rounded-xl ${status === 'loading' ? 'animate-pulse' : ''}`} data-id="element-234" data-mp="18irsf24wfl5a" />}
              </button>
              <p className="px-1 pb-1 pt-3 text-center text-xs font-semibold leading-snug text-slate-600" data-id="element-235" data-mp="2xxfw8ezfburp">{caption}</p>
            </li>)}
        </ul>
        <div className="mt-10 flex justify-center" data-id="element-236" data-mp="1un1y7j8bft3n">
          <CTAButton className="w-full max-w-md md:w-auto" data-id="element-237" data-mp="1zllo1uq15p1a" />
        </div>
      </Reveal>
      <PreviewLightbox item={openIndex !== null ? items[openIndex] : null} onClose={handleClose} data-id="element-238" data-mp="1llublyf5wgpe" />
    </section>;
}